import {
  FC,
  Ref,
  forwardRef,
  PropsWithoutRef,
  Children,
  cloneElement,
  ReactElement,
  ChangeEvent,
  useState,
  useEffect,
  useRef
} from 'react';
import styled from 'styled-components';

import { ForwardProps } from '../../types';
import FormField, { FormFieldProps } from '../FormField';
import { StyledFormField } from '../FormField/FormField';
import { FormControlProps } from '../FormControl';
import { RadioCheckProps } from '../RadioCheck';
import Grid from '../Grid';
import { useElement, useUID } from '../../hooks';

export interface RadioCheckGroupProps {
  /** Accepts Checkboxes or RadioButtons as children. */
  children: ReactElement | ReactElement[];
  /**
   * Conveniently pass an onChange handler to the group to be notified when any of the inputs change.
   * Additionally, onChange can be passed to individual children.
   * Both handlers will be called, child followed by group.
   */
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Set visual state based on a validation state. */
  status?: FormControlProps['status'];
  /** Pass a string or a fragment with an Icon and string. */
  label?: FormControlProps['label'];
  /** Visually hides the label region. */
  labelHidden?: FormControlProps['labelHidden'];
  /** It is recommended to pass a simple string to offer guidance. Text will be styled based on status prop. */
  info?: FormControlProps['info'];
  /** Indicate if the field is required. The browser defaults to false. */
  required?: FormControlProps['required'];
  /** Disable the control. The browser defaults to false. */
  disabled?: FormControlProps['disabled'];
  /** Makes the input non editable and non clickable. The browser defaults to false. */
  readOnly?: FormControlProps['readOnly'];
  /**
   * Layout field elements inline in a row.
   * @default false
   */
  inline?: FormFieldProps['inline'];
  /** Used to toggle the auto stacking feature of an inlined group.
   * @default true
   */
  autoStack?: boolean;
  /** Sets html name attribute for the underlying control. Useful for mapping to a data field. */
  name?: FormControlProps['name'];
  /**
   * Controls the styling of the child RadioChecks.
   * @default 'simple'
   */
  variant?: RadioCheckProps['variant'];
  /** Ref forwarded to the wrapping element. */
  ref?: Ref<HTMLElement>;
}

const defaultProps = {
  required: false,
  disabled: false
};

const StyledRadioCheckGroup = styled.fieldset`
  &[disabled] {
    > ${StyledFormField} {
      opacity: unset;
    }
  }
`;

const RadioCheckGroup: FC<RadioCheckGroupProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<RadioCheckGroupProps>, ref: RadioCheckGroupProps['ref']) => {
    const uid = useUID();
    const {
      children,
      name = uid,
      disabled,
      onChange,
      readOnly,
      inline = false,
      autoStack = true,
      variant = 'simple',
      ...restProps
    } = props;

    const [optionsEl, setOptionsEl] = useElement<HTMLDivElement>();
    const [renderInline, setRenderInline] = useState(inline);
    const renderInlineRef = useRef(inline);
    const minInlineWidth = useRef(0);

    useEffect(() => {
      if (inline && optionsEl && autoStack) {
        const intersectionObserver = new IntersectionObserver(
          entries => {
            if (entries[0].intersectionRatio < 1) {
              if (entries[0].rootBounds) minInlineWidth.current = entries[0].rootBounds.width + 1;
              renderInlineRef.current = false;
              setRenderInline(false);
            }
          },
          { root: optionsEl, threshold: 1 }
        );

        const resizeObserver = new ResizeObserver(entries => {
          if (!renderInlineRef.current && entries[0].contentRect.width > minInlineWidth.current) {
            renderInlineRef.current = true;
            setRenderInline(true);
          }
        });

        intersectionObserver.observe(optionsEl.lastChild as Element);
        resizeObserver.observe(optionsEl);

        return () => {
          intersectionObserver.disconnect();
          resizeObserver.disconnect();
        };
      }
    }, [optionsEl, inline, autoStack]);

    return (
      <FormField
        {...{
          as: StyledRadioCheckGroup,
          labelAs: 'legend',
          ref,
          name,
          disabled,
          ...restProps
        }}
      >
        <Grid
          ref={setOptionsEl}
          container={{
            colGap: 2,
            rowGap: variant === 'card' ? 1 : 0,
            cols: renderInline ? 'repeat(auto-fit, minmax(1ch, max-content))' : '1fr'
          }}
        >
          {Children.map(children, child =>
            cloneElement(child, {
              name,
              disabled: disabled || child.props.disabled,
              readOnly: readOnly || child.props.readOnly,
              variant,
              onChange: onChange
                ? (e: ChangeEvent<HTMLInputElement>) => {
                    if (!(readOnly || child.props.readOnly)) {
                      child.props.onChange?.(e);
                      onChange(e);
                    }
                  }
                : child.props.onChange
            })
          )}
        </Grid>
      </FormField>
    );
  }
);

RadioCheckGroup.defaultProps = defaultProps;

export default RadioCheckGroup;
